import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, OnDestroy, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { AuthConfig as OAuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, distinctUntilChanged, filter, Observable, Subject, Subscription } from 'rxjs';
import { AuthSession } from './auth.model';

export const AUTH_CONFIG = new InjectionToken<string>('portal-auth-config');

export type AuthConfig = OAuthConfig & {
    sessionUrl?: string;
};

/**
 * When `authConfig.oidc` is true, login uses Keycloak discovery and the session is built
 * from the access token. Otherwise pg-authserver `sessionUrl` path is used.
 */
@Injectable()
export class AuthService implements OnDestroy {
    private subscriptions: Subscription[] = [];
    private _session$ = new BehaviorSubject<AuthSession>(null);
    private _isAuthenticated$ = new BehaviorSubject<boolean>(false);
    private _isAuthenticated = false;

    get session() {
        return this._session$.getValue();
    }

    get isAuthenticated() {
        return this._isAuthenticated;
    }

    private _beforeLogOut$ = new Subject();
    beforeLogOut$ = this._beforeLogOut$.asObservable();

    constructor(
        @Optional() @Inject(AUTH_CONFIG) private authConfig: AuthConfig,
        private oauth: OAuthService,
        private http: HttpClient,
        private router: Router
    ) {
        this.oauth.configure(authConfig);

        this.oauth.events.pipe(filter((e) => e.type === 'logout')).subscribe(() => {
            this.logoutComplete();
        });

        this._isAuthenticated$
            .pipe(
                distinctUntilChanged(),
                filter((authenticated) => authenticated)
            )
            .subscribe(() => {
                this.processRedirect();
            });

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible' && !this.oauth.hasValidAccessToken()) {
                this.logoutComplete();
            }
        });

        window.addEventListener('storage', (event) => {
            if (
                event.key === 'access_token' &&
                document.visibilityState === 'visible' &&
                !this.oauth.hasValidAccessToken()
            ) {
                this.logoutComplete();
            }
        });
    }

    get session$(): Observable<AuthSession> {
        return this._session$.pipe(filter((data) => data !== null));
    }

    check(): Observable<boolean> {
        return new Observable<boolean>((observable) => {
            const afterCodeFlow = this.authConfig.oidc
                ? this.oauth.loadDiscoveryDocumentAndTryLogin()
                : this.oauth.tryLoginCodeFlow();
            afterCodeFlow
                .then(() => {
                    if (this.oauth.hasValidAccessToken()) {
                        if (this.authConfig.oidc) {
                            this.completeLogin(this.sessionFromAccessToken(), observable);
                            return;
                        }
                        this.subscriptions.push(
                            this.currentSessionRequest().subscribe({
                                next: (session: AuthSession) => {
                                    this.completeLogin(session, observable);
                                },
                                error: () => {
                                    this.logout();
                                },
                            })
                        );
                    } else {
                        observable.next(false);
                        observable.complete();
                    }
                })
                .catch((err) => {
                    if (this.authConfig.oidc) {
                        console.error('Auth check failed', err);
                        observable.next(false);
                        observable.complete();
                        return;
                    }
                    this.logout();
                });
        });
    }

    processRedirect() {
        if (!this.oauth.state && !this.authConfig.oidc) {
            return;
        }
        const state = this.oauth.state ? decodeURIComponent(this.oauth.state) : '';
        const redirectURL =
            state && state !== '/login' && state !== '/auth/login' ? state : '/';
        this.router.navigateByUrl(redirectURL, { replaceUrl: true });
    }

    currentSessionRequest() {
        return this.http.get(this.authConfig.sessionUrl);
    }

    authenticate(redirectURL?: string) {
        if (this.authConfig.oidc) {
            const startLogin = () => this.oauth.initLoginFlow(redirectURL);
            if (this.oauth.loginUrl) {
                startLogin();
                return;
            }
            this.oauth
                .loadDiscoveryDocumentAndTryLogin()
                .then(() => startLogin())
                .catch((err) => console.error('OIDC login failed', err));
            return;
        }
        this.logout();
        this.oauth.initLoginFlow(redirectURL);
    }

    logout() {
        this._beforeLogOut$.next(null);
        this.oauth.logOut(!this.authConfig.oidc);
    }

    ngOnDestroy() {
        this.subscriptions?.forEach((s) => s.unsubscribe());
    }

    private completeLogin(
        session: AuthSession,
        observable: { next: (value: boolean) => void; complete: () => void }
    ) {
        this._isAuthenticated = true;
        this._session$.next(session);
        observable.next(this._isAuthenticated);
        observable.complete();
        this._isAuthenticated$.next(this._isAuthenticated);
    }

    /** Maps Keycloak ID/access-token claims into the existing {@link AuthSession} shape. */
    private sessionFromAccessToken(): AuthSession {
        const claims = (this.oauth.getIdentityClaims() ?? {}) as Record<string, unknown>;
        const email = String(claims['email'] ?? claims['preferred_username'] ?? '');
        const name = String(claims['name'] ?? email);
        const roles = this.readAccessTokenRoles();
        const roleName = roles[0] ?? '';
        return {
            resource: {
                id: 0,
                created: '',
                lastModified: '',
                person: {
                    id: 0,
                    name,
                    clientId: '',
                    created: '',
                    lastModified: '',
                    createdBy: '',
                    lastModifiedBy: '',
                    requiredAuthType: '',
                    email,
                    firstName: '',
                    lastName: '',
                    status: 'A',
                    requirePasswordChange: 'N',
                    lastPasswordChange: '',
                    isApplication: 'N',
                },
                status: 'A',
                authType: 'OIDC',
                authToken: '',
                nonce: '',
                token: this.oauth.getAccessToken() ?? '',
                validTo: '',
                activeRole: {
                    clientId: roleName,
                    created: '',
                    createdBy: '',
                    id: 0,
                    isDefault: true,
                    lastModified: '',
                    lastModifiedBy: '',
                    organization: {
                        clientId: '',
                        created: '',
                        fullName: name,
                        id: 0,
                        lastModified: '',
                    },
                    person: { email, name },
                    role: {
                        clientId: roleName,
                        created: '',
                        description: '',
                        id: 0,
                        lastModified: '',
                        name: roleName,
                    },
                },
            },
        };
    }

    private readAccessTokenRoles(): string[] {
        const token = this.oauth.getAccessToken();
        if (!token) {
            return [];
        }
        try {
            // JWT payloads are base64url (no padding). atob requires standard base64.
            const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
            const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
            const json = JSON.parse(atob(padded)) as {
                realm_access?: { roles?: string[] };
            };
            return json.realm_access?.roles ?? [];
        } catch {
            return [];
        }
    }

    private logoutComplete() {
        if (!this._isAuthenticated) {
            return;
        }

        this._isAuthenticated = false;
        this._isAuthenticated$.next(this._isAuthenticated);

        this.router.navigate(['login'], { queryParams: { redirectURL: this.router.url } });
    }
}
